import type { Analysis } from '@ni/shared';
import type { StatusForAnnotation } from '../../lib/copy';
import {
  analysisCaveats,
  analysisOfKind,
  analysisProse,
  clubFitRows,
  conversationLists,
} from '../../lib/analysis-copy';
import { formatMonth } from '../../lib/format';
import { Caveats } from '../ui/caveats';
import { Prose } from '../ui/prose';
import { KeptFromEarlier } from './kept-from-earlier';
import { LabelledList } from './labelled-list';
import { Section } from '../ui/section';

/**
 * Fit and Conversation Notes, in the reading order rather than beside it
 * (docs/notes/2026-09-01-research-layout.md).
 *
 * These were the rail. The rail cost 38% of the page's width for content that
 * answers question 3 — "how would they fit in here?" — which the reader asks
 * before the evidence, not alongside it; and it started at the sections, so the
 * top right third of every dossier was empty, and on a parked run the whole
 * right third was. Both readings now sit in the column at the document's own
 * measure, and the tinted card is gone with the rail: a block that is in the
 * flow does not need a box to say it is a block.
 *
 * Both are still labeled as interpretation where they sit next to facts (rule
 * §3.9), and each reading still closes with its own caveats rather than a
 * shared bin — the hedging is about that reading.
 *
 * A reading the subject's current status would not ask for again keeps its
 * section, its head and its content, and gains one line saying so (design note
 * §2.5): a prospect promoted to member still shows the Fit that was written for
 * them, and the page says why it is not being refreshed rather than deleting it.
 *
 * The criteria are the one natively grid-shaped thing on the page, so they get
 * the grid. Conversation notes are the one natively list-shaped thing: three
 * short lists of one sentence each, which is how they are used — read once
 * before a meeting, not read through. They are set as lists rather than
 * flattened into a paragraph, and the paragraph remains only as the fallback
 * for the notes written before the lists were contracted.
 */
export function FitBlock({
  analyses,
  subject,
}: {
  analyses: Analysis[];
  /** The status block behind the kept-from-earlier line; absent, no line. */
  subject?: StatusForAnnotation;
}) {
  const clubFit = analysisOfKind(analyses, 'club_fit');
  const conversation = analysisOfKind(analyses, 'conversation_notes');
  const criteria = clubFitRows(clubFit);
  // Research completion is not a club-fit criterion. Keep the original row
  // count for the legacy prose fallback so filtering cannot bring it back.
  const fitRows = criteria.filter((row) => row.criterion.toLowerCase() !== 'diligence');
  const fitProse = criteria.length === 0 ? analysisProse(clubFit) : null;
  const showFit = clubFit && (criteria.length === 0 || fitRows.length > 0);
  const lists = conversationLists(conversation);
  const hasLists =
    lists.openers.length > 0 || lists.common_ground.length > 0 || lists.carry_lightly.length > 0;
  /* The fallback, and the only case it covers: a note written before the three
     lists were contracted, which has a paragraph and nothing else. */
  const conversationProse = hasLists ? null : analysisProse(conversation);

  if (!clubFit && !conversation) return null;

  return (
    <>
      {showFit ? (
        <Section lead title="Club fit">
          <div className="sub">
            Assessed against the club&rsquo;s values
            {clubFit.as_of ? ` · as of ${formatMonth(clubFit.as_of)}` : ''}
          </div>
          <KeptFromEarlier subject={subject} kind="club_fit" />

          {fitProse ? <Prose text={fitProse} className="notes" /> : null}

          {fitRows.length > 0 ? (
            <div className="fit-grid">
              {/* Index-composed for the same reason the talk lists are: the
                  criterion names come from the model, and two rows that happen
                  to share one would collide as keys. */}
              {fitRows.map((row, index) => (
                <div
                  className={row.met ? 'fit-row' : 'fit-row unmet'}
                  key={`${index}-${row.criterion}`}
                >
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    aria-hidden="true"
                  >
                    {row.met ? (
                      <path d="M2 6.3 L4.7 9 L10 2.8" />
                    ) : (
                      /* A cross at the check's own weight and box, so the two
                         marks sit on one optical line down the grid. Amber, not
                         red: an unmet value is a tension to raise, not a
                         finding against the man. */
                      <path d="M2.6 2.6 L9.4 9.4 M9.4 2.6 L2.6 9.4" />
                    )}
                  </svg>
                  <div>
                    <div className="criterion">{row.criterion}</div>
                    {row.note ? <div className="note">{row.note}</div> : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <Caveats caveats={analysisCaveats(clubFit)} />
        </Section>
      ) : null}

      {conversation ? (
        <Section title="Conversation notes">
          <div className="sub">Assessed from the findings.</div>
          <KeptFromEarlier subject={subject} kind="conversation_notes" />
          {conversationProse ? <Prose text={conversationProse} className="notes" /> : null}
          {hasLists ? (
            <div className="talk-lists">
              <LabelledList label="Open with" lines={lists.openers} />
              <LabelledList label="Common ground" lines={lists.common_ground} />
              <LabelledList label="Carry lightly" lines={lists.carry_lightly} />
            </div>
          ) : null}
          <Caveats caveats={analysisCaveats(conversation)} />
        </Section>
      ) : null}
    </>
  );
}
